// Dependencies
import React, {
  Ref,
  useState,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
  KeyboardEventHandler,
  useEffect,
  useMemo,
  useId,
} from 'react';
import { useClickAway } from 'react-use';
import { useTheme } from 'styled-components';

// Types
import {
  SearchableDropdownProps,
  ImperativeRefSearchableDropdown,
  OptionSelected,
} from './types';

// Styled Components
import {
  Container,
  DropDownHeader,
  DropDownContainer,
  Label,
  ArrowContainer,
  ArrowDown,
  SearchInput,
  DropDownListContainer,
  List,
  ListItem,
  NoResultsText,
  ErrorMessage,
} from './styles';
import { matchesSearch } from '../../../utils/textSearch';

const SearchableDropdown = (
  {
    inputSize,
    typeDropDown,
    placeholder,
    labelText,
    options,
    onChangeSelected,
    defaultValue,
    selectedOption: selectedOptionProp,
    error,
    borderError,
    isDisabled,
    handleSelectedOption,
    optionsChange = false,
    minSearchLength = 0,
    caseSensitive = false,
    noResultsText = 'No se encontraron resultados',
    // Extraído del spread para que SOLO aplique al <input> (línea inputId
    // abajo) y NO al <Container>. Antes el `{...props}` lo metía también
    // en el div del Container, generando dos elementos con el mismo id en
    // el DOM — los locators E2E por `#predefine-select` veían dos
    // elementos y disparaban "strict mode violation".
    id,
    ...props
  }: SearchableDropdownProps,
  innerRef: Ref<ImperativeRefSearchableDropdown>
): JSX.Element => {
  const theme = useTheme();
  const [isActive, setIsActive] = useState(false);
  const [selectedOption, setSelectedOption] = useState<
    OptionSelected | undefined
  >(() =>
    options?.find((opt) => {
      if (opt.value === defaultValue) {
        return opt.value === defaultValue;
      }
      if (opt.label === defaultValue) {
        return opt.label === defaultValue;
      }
    })
  );
  const [searchTerm, setSearchTerm] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Track previous selectedOptionProp to detect explicit clears (value → undefined transitions)
  const prevSelectedOptionPropRef = useRef<string | undefined>(selectedOptionProp);

  // Sincronizar selectedOption prop con estado local
  useEffect(() => {
    const prev = prevSelectedOptionPropRef.current;
    prevSelectedOptionPropRef.current = selectedOptionProp;

    if (selectedOptionProp !== undefined) {
      const option = options?.find((opt) => opt.value === selectedOptionProp);
      if (option) {
        setSelectedOption(option);
      } else if (selectedOptionProp === '' || selectedOptionProp === null) {
        setSelectedOption(undefined);
      }
    } else if (prev !== undefined) {
      // Explicit clear: selectedOptionProp transitioned from a value to undefined.
      // Do NOT clear when selectedOptionProp was always undefined and only options changed
      // (e.g. predefined order dropdowns that don't use selectedOption prop).
      setSelectedOption(undefined);
    }
  }, [selectedOptionProp, options]);

  // Filter options based on search term
  const { displayedOptions, hasMore } = useMemo(() => {
    // Guard against undefined/null options
    if (!options || !Array.isArray(options)) {
      return { displayedOptions: [], hasMore: false };
    }

    // Si no hay búsqueda activa (dropdown cerrado o sin escribir), mostrar TODAS
    if (!isActive || searchTerm.length < minSearchLength) {
      return { displayedOptions: options.slice(0, 200), hasMore: options.length > 200 };
    }

    // Por defecto sin mayúsculas, sin acentos y por palabras («eric» encuentra
    // «Èric», «roura anna» a «Anna Roura»): `matchesSearch`, la misma
    // normalización que el backend. `caseSensitive` conserva la comparación
    // literal de siempre.
    const filtered = options.filter((option) =>
      caseSensitive
        ? option.label.includes(searchTerm) || option.value.includes(searchTerm)
        : matchesSearch([option.label, option.value], searchTerm)
    );

    return {
      displayedOptions: filtered.slice(0, 200),
      hasMore: filtered.length > 200
    };
  }, [options, searchTerm, minSearchLength, caseSensitive, isActive]);

  useClickAway(containerRef, () => {
    setIsActive(false);
    // Limpiar búsqueda al cerrar
    setSearchTerm('');
  });

  const onChangeList = useCallback(
    (option: OptionSelected) => {
      setSelectedOption(option);
      setSearchTerm(''); // Limpiar búsqueda al seleccionar
      setIsActive(false);

      if (onChangeSelected) {
        onChangeSelected(option);
      }
      if (handleSelectedOption) {
        handleSelectedOption(option.value);
      }
    },
    [onChangeSelected, handleSelectedOption]
  );

  const handleHeaderClick = useCallback(() => {
    if (isDisabled) return;

    setIsActive(true);
    setSearchTerm(''); // Limpiar para mostrar todas las opciones

    // Focus y cursor al inicio (NO seleccionar)
    // En móvil/tablet, NO hacer focus para evitar abrir el teclado
    // Usamos los breakpoints del tema: landscapeTablet cubre tablets horizontales y verticales
    const isTabletOrMobile = window.matchMedia(
      `(max-width: ${theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem)`
    ).matches;

    if (!isTabletOrMobile) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.setSelectionRange(0, 0);
      }, 0);
    }
  }, [isDisabled, theme]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchTerm(value);

      if (!isActive) {
        setIsActive(true);
      }
    },
    [isActive]
  );

  const handleInputFocus = useCallback(() => {
    if (!isActive) {
      setIsActive(true);
      setSearchTerm(''); // Limpiar al hacer focus
    }
  }, [isActive]);

  useImperativeHandle(innerRef, () => ({
    clearSelected: () => {
      setSelectedOption(undefined);
      setSearchTerm('');
      setIsActive(false);
    },
    setSelectedOption: (option) => {
      if (option) {
        setSelectedOption(option);
        setSearchTerm('');
        if (onChangeSelected) {
          onChangeSelected(option);
        }
        if (handleSelectedOption) {
          handleSelectedOption(option.value);
        }
      }
    },
  }));

  const pressFocus: KeyboardEventHandler = useCallback(
    (event) => {
      if (event.key === ' ' && !isActive) {
        event.preventDefault();
        handleHeaderClick();
      }
    },
    [isActive, handleHeaderClick]
  );

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        setIsActive(false);
        setSearchTerm('');
        inputRef.current?.blur();
      } else if (event.key === 'Enter' && displayedOptions.length > 0) {
        // Select first option on Enter
        event.preventDefault();
        onChangeList(displayedOptions[0]);
        inputRef.current?.blur();
      } else if (event.key === 'ArrowDown' && !isActive) {
        event.preventDefault();
        setIsActive(true);
        setSearchTerm('');
      }
    },
    [displayedOptions, isActive, onChangeList]
  );

  // Determinar el valor del input y placeholder
  // - Si está escribiendo (isActive && searchTerm): muestra lo que escribe
  // - Si está abierto sin escribir (isActive && !searchTerm): input vacío, muestra todas las opciones
  // - Si está cerrado con selección: placeholder = seleccionado (negro)
  // - Si está cerrado sin selección: placeholder = "Selecciona" (gris)
  const inputValue = searchTerm;
  // La etiqueta visible se busca EN VIVO en `options` por `value` — ver el
  // comentario largo en `DropDownInput`: `selectedOption` es una foto y se
  // quedaba en el idioma anterior al cambiar de idioma con el campo ya
  // relleno. Si el valor no está en la lista se conserva la guardada.
  const selectedLabel = selectedOption
    ? (options?.find((opt) => opt.value === selectedOption.value)?.label ??
       selectedOption.label)
    : undefined;
  const inputPlaceholder = selectedLabel ?? placeholder;
  const hasSelectedValue = !!selectedOption;

  // Generate unique ID for accessibility if not provided
  const uniqueId = useId();
  const inputId = id || `searchable-dropdown-${uniqueId}`;

  return (
    <Container
      inputSize={inputSize}
      labelText={!!labelText}
      typeDropDown={typeDropDown}
      {...props}
    >
      {labelText && (
        <Label colorText="black" component="label" htmlFor={inputId} data-testid="searchable-dropdown-label">
          {labelText}
        </Label>
      )}
      <DropDownContainer ref={containerRef}>
        <DropDownHeader
          typeDropDown={typeDropDown}
          isActive={isActive}
          borderError={borderError}
          tabIndex={0}
          onKeyDownCapture={pressFocus}
          isDisabled={isDisabled}
          onClick={handleHeaderClick}
        >
          <SearchInput
            ref={inputRef}
            id={inputId}
            data-testid="searchable-dropdown-input"
            type="text"
            placeholder={inputPlaceholder}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            disabled={isDisabled}
            onClick={(e) => e.stopPropagation()}
            hasSelectedValue={hasSelectedValue}
          />
          <ArrowContainer
            onClick={(e) => {
              e.stopPropagation();
              handleHeaderClick();
            }}
          >
            <ArrowDown />
          </ArrowContainer>
        </DropDownHeader>

        {isActive && (
          <DropDownListContainer>
            <List role="listbox" typeDropDown={typeDropDown}>
              {displayedOptions.length > 0 ? (
                <>
                  {displayedOptions.map((option, index) => (
                    <ListItem
                      key={`${option.value}-${index}`}
                      tabIndex={0}
                      type={typeDropDown}
                      role="option"
                      aria-selected={selectedOption?.value === option.value}
                      isActive={selectedOption?.value === option.value}
                      onClick={(e) => {
                        e.stopPropagation();
                        onChangeList(option);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === ' ' || event.key === 'Enter') {
                          event.preventDefault();
                          onChangeList(option);
                        }
                      }}
                    >
                      {option.label}
                    </ListItem>
                  ))}
                  {hasMore && (
                    <NoResultsText>
                      Sigue escribiendo para ver más resultados...
                    </NoResultsText>
                  )}
                </>
              ) : (
                <NoResultsText>{noResultsText}</NoResultsText>
              )}
            </List>
          </DropDownListContainer>
        )}
      </DropDownContainer>
      {error && (
        <ErrorMessage colorText="lightCoralRed" size="XXS">
          {error}
        </ErrorMessage>
      )}
    </Container>
  );
};

export default forwardRef(SearchableDropdown);

